import type {
  DashboardOverview,
  AcquisitionReport,
  GeographyReport,
  ClickActivity,
  EvaluationResult,
  ConditionModule,
  TrafficEvent,
} from "@juicytraffic/contracts";

export const mockOverview: DashboardOverview = {
  window: "24h",
  total_clicks: 13_603,
  valid_clicks: 12_196,
  suspicious_clicks: 1_147,
  invalid_clicks: 260,
  flagged_clicks: 1_407,
  total_events: 13_603,
  suspicious_events: 1_147,
  risk_rate: 8.4,
  conversions: 678,
  conversion_rate: 4.98,
  data_confidence_rate: 93.7,
  freshness_at: new Date().toISOString(),
  change: { events: 12.4, risk: -2.1, conversions: 8.7 },
  series: Array.from({ length: 24 }, (_, index) => {
    const total = Math.round(390 + Math.sin(index / 2.8) * 90 + ((index * 29) % 110));
    const suspicious = Math.round(total * (0.055 + ((index * 7) % 4) / 100));
    const invalid = Math.round(total * (0.01 + ((index * 5) % 2) / 100));
    return {
      time: `${index.toString().padStart(2, "0")}:00`,
      valid: total - suspicious - invalid,
      suspicious,
      invalid,
    };
  }),
};

type ReportWindow = "24h" | "7d" | "30d";

const windowMultiplier: Record<ReportWindow, number> = { "24h": 1, "7d": 6.4, "30d": 25.7 };

// These rows deliberately reconcile to mockOverview for the 24-hour view.
// The demo should tell one coherent story regardless of which breakdown a
// visitor explores next.
const mockSourceRows: AcquisitionReport["rows"] = [
  { id: "taboola-us", label: "taboola-us", source_id: "taboola-us", total_clicks: 5200, valid_clicks: 4660, suspicious_clicks: 430, invalid_clicks: 110, conversions: 260 },
  { id: "native-hub", label: "native-hub", source_id: "native-hub", total_clicks: 3100, valid_clicks: 2790, suspicious_clicks: 260, invalid_clicks: 50, conversions: 160 },
  { id: "search-direct", label: "search-direct", source_id: "search-direct", total_clicks: 2400, valid_clicks: 2180, suspicious_clicks: 180, invalid_clicks: 40, conversions: 120 },
  { id: "email-042", label: "email-042", source_id: "email-042", total_clicks: 1700, valid_clicks: 1540, suspicious_clicks: 135, invalid_clicks: 25, conversions: 85 },
  { id: "unknown", label: "unknown", source_id: "unknown", total_clicks: 1203, valid_clicks: 1026, suspicious_clicks: 142, invalid_clicks: 35, conversions: 53 },
];

export function mockAcquisitionFor(window: ReportWindow): AcquisitionReport {
  const multiplier = windowMultiplier[window];
  return {
    dimension: "source",
    rows: mockSourceRows.map((row) => ({
      ...row,
      total_clicks: Math.round(row.total_clicks * multiplier),
      valid_clicks: Math.round(row.valid_clicks * multiplier),
      suspicious_clicks: Math.round(row.suspicious_clicks * multiplier),
      invalid_clicks: Math.round(row.invalid_clicks * multiplier),
      conversions: Math.round(row.conversions * multiplier),
    })),
  };
}

export const mockOverviewFor = (window: ReportWindow): DashboardOverview => {
  const multiplier = windowMultiplier[window];
  const pointCount = window === "24h" ? 24 : window === "7d" ? 7 : 30;
  const now = Date.now();
  const interval = window === "24h" ? 60 * 60_000 : 24 * 60 * 60_000;
  return {
    ...mockOverview,
    window,
    total_clicks: Math.round(mockOverview.total_clicks * multiplier),
    valid_clicks: Math.round(mockOverview.valid_clicks * multiplier),
    suspicious_clicks: Math.round(mockOverview.suspicious_clicks * multiplier),
    invalid_clicks: Math.round(mockOverview.invalid_clicks * multiplier),
    flagged_clicks: Math.round(mockOverview.flagged_clicks * multiplier),
    total_events: Math.round(mockOverview.total_events * multiplier),
    suspicious_events: Math.round(mockOverview.suspicious_events * multiplier),
    conversions: Math.round(mockOverview.conversions * multiplier),
    series: Array.from({ length: pointCount }, (_, index) => {
      const sample = mockOverview.series[index % mockOverview.series.length];
      const pointMultiplier = window === "24h" ? 1 : multiplier / pointCount;
      const timestamp = new Date(now - (pointCount - 1 - index) * interval);
      return {
        valid: Math.round(sample.valid * pointMultiplier),
        suspicious: Math.round(sample.suspicious * pointMultiplier),
        invalid: Math.round(sample.invalid * pointMultiplier),
        time: window === "24h" ? sample.time : timestamp.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      };
    }),
  };
};

export const mockGeography = (dimension: GeographyReport["dimension"], window: ReportWindow = "24h"): GeographyReport => {
  const multiplier = windowMultiplier[window];
  const locations = [
    { country: "United States", region: "California", region_code: "CA", city: "Los Angeles", total: 3480, invalid: 94, suspicious: 211 },
    { country: "United States", region: "New York", region_code: "NY", city: "New York", total: 2800, invalid: 108, suspicious: 236 },
    { country: "Germany", region: "Berlin", region_code: "BE", city: "Berlin", total: 1840, invalid: 19, suspicious: 72 },
    { country: "Brazil", region: "São Paulo", region_code: "SP", city: "São Paulo", total: 1570, invalid: 57, suspicious: 128 },
    { country: "United Kingdom", region: "England", region_code: "ENG", city: "London", total: 1460, invalid: 21, suspicious: 66 },
    { country: "Canada", region: "Ontario", region_code: "ON", city: "Toronto", total: 980, invalid: 9, suspicious: 31 },
    { country: "Philippines", region: "Metro Manila", region_code: "00", city: "Manila", total: 760, invalid: 38, suspicious: 78 },
  ];
  const grouped = new Map<string, GeographyReport["rows"][number]>();
  locations.forEach((location) => {
    const id = dimension === "country" ? location.country : dimension === "region" ? `${location.country}:${location.region}` : `${location.country}:${location.region}:${location.city}`;
    const label = dimension === "country" ? location.country : dimension === "region" ? location.region : location.city;
    const row = grouped.get(id) || { id, label, country: location.country, region: location.region, region_code: location.region_code, city: location.city, total_clicks: 0, valid_clicks: 0, suspicious_clicks: 0, invalid_clicks: 0, conversions: 0 };
    row.total_clicks += Math.round(location.total * multiplier);
    row.suspicious_clicks += Math.round(location.suspicious * multiplier);
    row.invalid_clicks += Math.round(location.invalid * multiplier);
    row.valid_clicks = row.total_clicks - row.suspicious_clicks - row.invalid_clicks;
    row.conversions += Math.round(location.total * multiplier * 0.042);
    grouped.set(id, row);
  });
  return { dimension, rows: [...grouped.values()].sort((a, b) => b.total_clicks - a.total_clicks) };
};

export const mockModules: ConditionModule[] = [
  {
    schema_version: "1", id: "probe_missing",
    name: "Browser probe not observed",
    description: "Applies only to Use my domain clicks: the server recorded the paid arrival but browser-probe evidence has not joined it. Default-probe traffic is excluded.",
    version: 1, state: "published", enabled: true, outcome: "suspicious", applies_to: ["outcome"],
    match: { mode: "all", conditions: [{ field: "telemetry.server_signed", operator: "eq", value: true }, { field: "telemetry.probe_present", operator: "eq", value: false }, { field: "properties.probe_status", operator: "eq", value: "not_observed" }] },
    reason_code: "probe_missing", created_at: "2026-08-16T00:00:00.000Z", updated_at: "2026-08-16T00:00:00.000Z", published_at: "2026-08-16T00:00:00.000Z",
  },
  {
    schema_version: "1", id: "network_velocity_1h",
    name: "Repeated network velocity",
    description: "Network identifier appears 20+ times in a rolling hour.",
    version: 3, state: "published", enabled: true, outcome: "suspicious", applies_to: ["click_received"],
    match: { mode: "all", conditions: [{ field: "edge.counts.one_hour", operator: "gte", value: 20 }] },
    reason_code: "network_velocity_1h", created_at: "2026-07-10T08:00:00.000Z", updated_at: "2026-07-28T08:00:00.000Z", published_at: "2026-07-28T08:00:00.000Z",
  },
  {
    schema_version: "1", id: "synthetic_interaction_pattern",
    name: "Synthetic interaction pattern",
    description: "A browser-side heuristic is suspicious evidence, not proof of fraud.",
    version: 1, state: "published", enabled: true, outcome: "suspicious", applies_to: ["click_checkpoint_30", "click_snapshot", "click_summary_60"],
    match: { mode: "all", conditions: [{ field: "context.interaction_pattern", operator: "eq", value: "synthetic_like" }] },
    reason_code: "synthetic_interaction_pattern", created_at: "2026-08-15T00:00:00.000Z", updated_at: "2026-08-15T00:00:00.000Z", published_at: "2026-08-15T00:00:00.000Z",
  },
  {
    schema_version: "1", id: "inactive_ua_mismatch", name: "Inactive session with browser mismatch",
    description: "No interaction after 15 seconds and the browser context disagrees with the request header.",
    version: 1, state: "published", enabled: true, outcome: "invalid", applies_to: ["click_checkpoint_30", "click_snapshot", "click_summary_60"],
    match: { mode: "all", conditions: [{ field: "context.active_time_bucket_ms", operator: "gte", value: 15000 }, { field: "context.pointer_activity_bucket", operator: "eq", value: "none" }, { field: "context.user_agent_consistent", operator: "eq", value: false }] },
    reason_code: "inactive_ua_mismatch", created_at: "2026-08-14T00:00:00.000Z", updated_at: "2026-08-14T00:00:00.000Z", published_at: "2026-08-14T00:00:00.000Z",
  },
];

const sourceNames = ["taboola-us", "native-hub", "search-direct", "email-042", "unknown"];
const locations = ["US", "DE", "BR", "GB", "CA", "NL", "PH"];
const eventKinds: TrafficEvent["kind"][] = ["page_view", "click", "page_view", "click", "conversion"];

export const mockEvents: Array<{ event: TrafficEvent; risk: EvaluationResult }> = Array.from(
  { length: 36 },
  (_, index) => {
    const risky = index % 5 === 0 || index % 11 === 0;
    const critical = index % 11 === 0;
    const ageMinutes = index < 12 ? index * 15 : index < 24 ? 24 * 60 + (index - 12) * 8 * 60 : 7 * 24 * 60 + (index - 24) * 36 * 60;
    const occurredAt = new Date(Date.now() - ageMinutes * 60_000).toISOString();
    const probe = index % 9 !== 0;
    const event: TrafficEvent = {
      schema_version: "1",
      event_id: `evt_${(99438 - index).toString(36)}`,
      trace_id: `tr_${(29438 - index).toString(36)}`,
      tenant_id: "org_demo",
      site_id: index % 3 === 0 ? "site_checkout" : "site_demo",
      site_key: "jt_public_demo",
      kind: eventKinds[index % eventKinds.length],
      occurred_at: occurredAt,
      received_at: occurredAt,
      session_id: `ses_${Math.floor(index / 2).toString(36)}`,
      page_id: `page_${index.toString(36)}`,
      click_id: `clk_${(4000 + index).toString(36)}`,
      campaign_id: ["summer-native", "retarget-07", "direct-brand"][index % 3],
      source_id: sourceNames[index % sourceNames.length],
      placement_id: `plc_${(index % 7) + 1}`,
      context: {
        url: `https://offers.example/landing-${(index % 4) + 1}`,
        referrer: index % 4 ? "https://publisher.example" : undefined,
        language: index % 3 ? "en-US" : "de-DE",
        timezone: index % 3 ? "America/New_York" : "Europe/Berlin",
        screen_bucket: index % 2 ? "sm" : "lg",
        visibility: "visible",
        navigation_ms: 280 + index * 17,
        probe_load_ms: 18 + (index % 12),
        ua_mobile: index % 3 !== 0,
        ua_browser: index % 4 === 0 ? "Safari" : "Chrome",
      },
      edge: {
        country: locations[index % locations.length],
        continent: ["NA", "EU", "SA", "EU", "NA", "EU", "AS"][index % locations.length],
        city: ["Portland", "Berlin", "São Paulo", "London", "Toronto", "Amsterdam", "Manila"][index % locations.length],
        region: ["Oregon", "Berlin", "São Paulo", "England", "Ontario", "North Holland", "Metro Manila"][index % locations.length],
        region_code: ["OR", "BE", "SP", "ENG", "ON", "NH", "00"][index % locations.length],
        postal_code: ["97205", "10117", "01000", "EC1A", "M5H", "1012", "1000"][index % locations.length],
        latitude: [45.5231, 52.52, -23.5505, 51.5072, 43.6532, 52.3676, 14.5995][index % locations.length],
        longitude: [-122.6765, 13.405, -46.6333, -0.1276, -79.3832, 4.9041, 120.9842][index % locations.length],
        timezone: ["America/Los_Angeles", "Europe/Berlin", "America/Sao_Paulo", "Europe/London", "America/Toronto", "Europe/Amsterdam", "Asia/Manila"][index % locations.length],
        asn: risky ? 16509 : 7922 + index,
        colo: ["IAD", "FRA", "GRU", "LHR"][index % 4],
        http_protocol: "HTTP/3",
        tls_version: "TLSv1.3",
        ip_hash: `hash_${Math.floor(index / 2).toString(36)}`,
        ip_address: `${[203, 198, 192][Math.floor(index / 2) % 3]}.${51 + (Math.floor(index / 2) % 4)}.${100 + (Math.floor(index / 2) % 9)}.${20 + Math.floor(index / 2)}`,
        counts: {
          five_minutes: risky ? 9 + (index % 7) : 1 + (index % 2),
          one_hour: risky ? 22 + index : 2 + (index % 6),
          twenty_four_hours: risky ? 90 + index : 4 + index,
        },
      },
      telemetry: { probe_present: probe, server_signed: index % 5 === 4 },
    };
    return {
      event,
      risk: {
        event_id: event.event_id,
        traffic_status: critical ? "invalid" : risky ? "suspicious" : "valid",
        data_confidence: probe ? "high" : "low",
        reason_codes: critical
          ? ["click_burst_5m", "repeat_ip_1h", "datacenter_asn"]
          : risky
            ? ["click_burst_5m", "repeat_ip_1h"]
            : [],
        matched_modules: risky ? [{ module_id: "network_velocity_1h", module_version: 3, outcome: critical ? "invalid" : "suspicious", reason_code: "network_velocity_1h" }] : [],
        evaluated_at: occurredAt,
        engine_version: "condition-modules-1.2.0",
      },
    };
  },
);

export const mockClickActivities: ClickActivity[] = mockEvents.map(({ event, risk }, index) => ({
  click_event_id: event.event_id,
  click_record_id: event.click_record_id || event.click_id || event.event_id,
  click_id: event.click_id || event.event_id,
  external_click_id: event.click_id,
  site_id: event.site_id,
  session_id: event.session_id || event.event_id,
  ip_hash: event.edge.ip_hash,
  ip_risk_token: event.edge.ip_risk_token || event.edge.ip_hash,
  ip_address: event.edge.ip_address,
  occurred_at: event.occurred_at,
  last_seen_at: event.received_at,
  probe_status: "executed",
  probe_executed_at: event.received_at,
  // Keep the newest demo click unresolved so the console demonstrates the
  // immediate-row / delayed-decision lifecycle without mislabelling older rows.
  assessment_state: index === 0 ? "evaluating" : "finalized",
  timed_out: false,
  traffic_status: risk.traffic_status,
  data_confidence: risk.data_confidence,
  reason_codes: risk.reason_codes,
  matched_modules: risk.matched_modules,
  country: event.edge.country,
  continent: event.edge.continent,
  city: event.edge.city,
  region: event.edge.region,
  region_code: event.edge.region_code,
  postal_code: event.edge.postal_code,
  latitude: event.edge.latitude,
  longitude: event.edge.longitude,
  network_timezone: event.edge.timezone,
  asn: event.edge.asn,
  colo: event.edge.colo,
  network_intelligence: {
    source: "x4b-lists-vpn",
    source_version: "0123456789abcdef0123456789abcdef01234567",
    lookup_status: index % 4 === 3 ? "unavailable" : index % 4 === 2 ? "not_matched" : "matched",
    classification: index % 4 === 0 ? "vpn" : index % 4 === 1 ? "hosting_or_datacenter" : "none",
    vpn_range_match: index % 4 === 0,
    datacenter_range_match: index % 4 === 1,
    retrieved_at: event.received_at,
  },
  campaign_id: event.campaign_id,
  source_id: event.source_id,
  placement_id: event.placement_id,
  target_url: event.context?.url,
  referrer: event.context?.referrer,
  device_type: event.context?.ua_mobile ? "Mobile" : "Desktop",
  browser: event.context?.ua_browser,
  platform: event.context?.ua_platform,
  timezone: event.context?.timezone,
  browser_token_hash: event.browser_token_hash,
  interaction_level: (event.context?.active_time_bucket_ms || 0) >= 60_000 ? "high" : (event.context?.active_time_bucket_ms || 0) >= 30_000 ? "medium" : "low",
  automation_score: event.context?.automation_webdriver ? 85 : risk.traffic_status !== "valid" ? 35 : 5,
  automation_level: event.context?.automation_webdriver ? "high" : risk.traffic_status !== "valid" ? "medium" : "low",
  assessment_version: "condition-modules-1.2.0",
  converted: event.kind === "conversion",
  session_click_count: mockEvents.filter((row) => row.event.session_id === event.session_id).length,
  ip_click_count: mockEvents.filter((row) => row.event.edge.ip_hash === event.edge.ip_hash).length,
  page_view_count: event.kind === "page_view" ? 1 : 0,
}));
